import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaPlus, FaUpload, FaReceipt, FaTrash, FaCheckCircle, FaEllipsisH, FaChartLine
} from 'react-icons/fa';
import { Receipt, Wallet, FileText, TrendingUp, ChevronLeft } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import TendenciaAnualModal from './TendenciaAnualModal';
import '../AssetsView.css';
import { SURA_BLUE, SURA_BLUE2, SURA_TEAL, mesesVisibles } from './palette';

/* ─── Custom Tooltip ───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">Gasto</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const MESES_ORDEN = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const CENTROS_COSTO_OPTIONS = [
  '001 - Centro de Distribución', '002 - Share Service', '003 - Presidencia',
  '004 - Gerencia General', '005 - Finanzas', '006 - Gestion Humana',
  '007 - TI', '008 - Mercadeo', '009 - Comercial', '010 - Servicios Generales',
  '011 - Almacen', '012 - Distribucion', '013 - Seguridad Industrial',
  '014 - Calidad', '015 - Supply Chain', '016 - Comercio Exterior',
  '017 - Transporte', '018 - Trade Marketing', '019 - PCP'
];

const PresupuestoGeneralView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concepto, setConcepto] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [mesSeleccionado, setMesSeleccionado] = useState(MESES_ORDEN[new Date().getMonth()]);
  const [filtroSede, setFiltroSede] = useState('');
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [gastoAEliminar, setGastoAEliminar] = useState(null);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conceptosRes, gastosRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/generales/${id}`)
      ]);
      setConcepto(conceptosRes.data.find(c => c.id === parseInt(id)));
      setGastos(gastosRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProyecto = async () => {
    try {
      const res = await axiosInstance.post(`/api/presupuestos/generales/${id}/gasto`, {
        mes: 'ANUAL',
        anio: anioSeleccionado,
        detalle: 'Nuevo Proyecto',
        sede: '',
        gasto_dolar: 0
      });
      if (res.status === 201) {
        setGastos([...gastos, res.data.gasto]);
        toast.success("Proyecto agregado");
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al agregar proyecto');
    }
  };

  const handleDeleteGasto = async (gastoId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/generales/gasto/${gastoId}`);
      toast.success('Registro eliminado');
      setGastos(prev => prev.filter(g => g.id !== gastoId));
    } catch (error) {
      toast.error('Error al eliminar el registro');
    } finally {
      setGastoAEliminar(null);
    }
  };

  const handleInlineSave = async (gastoId, field, value) => {
    try {
      const res = await axiosInstance.put(`/api/presupuestos/generales/gasto/${gastoId}`, {
        [field]: value
      });
      if (res.status === 200) {
        setGastos(prev => prev.map(g => g.id === gastoId ? { ...g, [field]: value } : g));
        toast.success("Actualizado correctamente");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error actualizando gasto:", error);
      toast.error("Error al actualizar");
      return false;
    }
  };

  const gastoTotalAnual = useMemo(() => gastos.filter(g => g.anio === anioSeleccionado).reduce((acc, curr) => acc + Number(curr.gasto_dolar || 0), 0), [gastos, anioSeleccionado]);

  const cantidadRegistros = useMemo(() => gastos.filter(g => g.anio === anioSeleccionado).length, [gastos, anioSeleccionado]);

  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => {
      const total = gastos
        .filter(g => g.mes === mes && g.anio === anioSeleccionado)
        .reduce((acc, curr) => acc + Number(curr.gasto_dolar || 0), 0);
      return { name: mes.substring(0, 3), fullMonth: mes, total };
    });
  }, [gastos, anioSeleccionado]);

  const gastosFiltrados = useMemo(() => {
    let filtrados = gastos.filter(g => g.anio === anioSeleccionado);
    if (filtroSede) {
      filtrados = filtrados.filter(g => g.sede === filtroSede);
    }
    return filtrados;
  }, [gastos, anioSeleccionado, filtroSede]);

  /* Gastos por centro de costo (sede) del mes/año seleccionado */
  const ccData = useMemo(() => {
    const map = {};
    gastos
      .filter(g => g.anio === anioSeleccionado && g.mes === mesSeleccionado)
      .forEach(g => {
        const cc = g.sede || 'Sin asignar';
        map[cc] = (map[cc] || 0) + Number(g.gasto_dolar || 0);
      });
    return Object.entries(map)
      .map(([centro_costo, total]) => ({ centro_costo, total }))
      .sort((a, b) => b.total - a.total);
  }, [gastos, anioSeleccionado, mesSeleccionado]);

  const columns = useMemo(() => [
    {
      header: 'Mes',
      accessorKey: 'mes',
      size: 130,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.mes}
          options={MESES_ORDEN}
          onSave={(val) => handleInlineSave(row.original.id, 'mes', val)}
        />
      )
    },
    {
      header: 'Descripción / Detalle',
      accessorKey: 'detalle',
      cell: ({ row }) => (
        <div className="font-medium text-gray-800">
          <EditableTextCell
            value={row.original.detalle}
            placeholder="Ingrese descripción..."
            onSave={(val) => handleInlineSave(row.original.id, 'detalle', val)}
          />
        </div>
      )
    },
    {
      header: 'Sede / Ubicación',
      accessorKey: 'sede',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.sede}
          placeholder="Ej: 001 - Centro de Distribución"
          options={CENTROS_COSTO_OPTIONS}
          onSave={(val) => handleInlineSave(row.original.id, 'sede', val)}
        />
      )
    },
    {
      header: 'Monto Total',
      accessorKey: 'gasto_dolar',
      cell: ({ row }) => (
        <div className="flex justify-end items-center font-semibold font-mono" style={{ color: SURA_TEAL }}>
          <span className="mr-1">$</span>
          <EditableTextCell
            type="number"
            decimals={2}
            value={row.original.gasto_dolar}
            onSave={(val) => handleInlineSave(row.original.id, 'gasto_dolar', val)}
          />
        </div>
      )
    },
    {
      id: 'acciones',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>

          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                {
                  icon: FaChartLine,
                  label: 'Ver tendencia anual',
                  color: SURA_BLUE2,
                  onClick: () => {
                    const key = (row.original.detalle || '').trim();
                    const serie = mesesVisibles(anioSeleccionado).map(mes => {
                      const items = gastos.filter(g => g.anio === anioSeleccionado && g.mes === mes && (g.detalle || '').trim() === key);
                      if (!items.length) return null;
                      return { name: mes.substring(0, 3), fullMonth: mes, total: items.reduce((s, g) => s + Number(g.gasto_dolar || 0), 0) };
                    }).filter(Boolean);
                    setTrendItem({ titulo: row.original.detalle || 'Registro', subtitulo: row.original.sede || '', data: serie });
                    setActiveActionMenu(null);
                  }
                },
                {
                  icon: FaTrash,
                  label: 'Eliminar Registro',
                  color: '#EF4444',
                  onClick: () => {
                    setGastoAEliminar(row.original.id);
                    setActiveActionMenu(null);
                  }
                }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ], [anioSeleccionado, activeActionMenu, menuAnchorRect, gastos]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: SURA_BLUE }}></div>
      </div>
    );
  }

  if (!concepto) return <div>Concepto no encontrado</div>;

  return (
    <div className="assets-view">

      {/* ── Hero Header ──────────────────────────────────────────── */}
      <div
        className="rounded-2xl mb-6 px-7 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative"
        style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10" style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5" style={{ background: '#fff', transform: 'translateY(40%)' }} />
        </div>

        <div className="flex items-center gap-4 z-10">
          <button
            onClick={() => navigate('/presupuestos')}
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium transition-colors"
          >
            <ChevronLeft size={16} />
            Presupuestos
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Receipt size={22} color="#fff" />
          </div>
          <div>
            <h2 className="text-white text-xl font-bold m-0 leading-tight">{concepto.nombre}</h2>
            <p className="text-white/60 text-xs m-0 mt-0.5">Control de servicios y proyectos generales</p>
          </div>
        </div>

        {/* Header controls */}
        <div className="flex flex-wrap items-center gap-3 z-10">
          <CustomSelect
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
            value={mesSeleccionado}
            onChange={e => setMesSeleccionado(e.target.value)}
          >
            {MESES_ORDEN.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>
          <CustomSelect
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
            value={anioSeleccionado}
            onChange={e => setAnioSeleccionado(parseInt(e.target.value))}
          >
            <option value={2026}>2026</option>
            <option value={2025}>2025</option>
          </CustomSelect>

          <button 
            onClick={handleAddProyecto}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all"
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)' }}
          >
            <FaPlus size={12} /> Agregar Proyecto
          </button>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            GENERAL
          </span>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Gasto Total ({anioSeleccionado})</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <Wallet size={17} style={{ color: SURA_BLUE }} />
            </div>
          </div>
          <p className="text-2xl font-bold font-mono text-gray-900">
            ${gastoTotalAnual.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE2 }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Registros ({anioSeleccionado})</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <FileText size={17} style={{ color: SURA_BLUE2 }} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{cantidadRegistros}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_TEAL }} />
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tendencia Anual</p>
            <TrendingUp size={14} style={{ color: SURA_TEAL }} />
          </div>
          <div className="h-16 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradGeneral" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SURA_TEAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SURA_TEAL} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke={SURA_TEAL} strokeWidth={2} fill="url(#gradGeneral)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Gastos por Centro de Costo ─────────────────────────── */}
      <div className="mb-6">
        <GastosPorCentroCosto
          data={ccData}
          subtitle={`${mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} ${anioSeleccionado}`}
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <TableView
          data={gastosFiltrados}
          columns={columns} 
          searchPlaceholder="Buscar por detalle o sede..."
          customActions={
            <CustomSelect
              className="px-3 py-1.5 rounded-xl text-sm font-medium border-2 border-gray-200 outline-none text-gray-600 bg-white hover:border-gray-300 focus:border-sura-blue transition-colors cursor-pointer"
              value={filtroSede}
              onChange={e => setFiltroSede(e.target.value)}
            >
              <option value="">Todas las Sedes</option>
              {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </CustomSelect>
          }
        />
      </div>

      <ConfirmDeleteModal
        isOpen={gastoAEliminar !== null}
        onClose={() => setGastoAEliminar(null)}
        onConfirm={() => handleDeleteGasto(gastoAEliminar)}
        message="¿Seguro que deseas eliminar este registro? Esta acción no se puede deshacer."
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />
    </div>
  );
};

export default PresupuestoGeneralView;
